/**
 * Dialogo que exige un motivo antes de una accion destructiva.
 *
 * Descartar una dead letter o cerrar una cuarentena sin decir por que es
 * exactamente lo que este sistema evita: el motivo viaja a la API y queda
 * GUARDADO en la fila (`resolution_note`), ademas del audit log, asi que se
 * puede leer despues desde la UI. El minimo de 3 caracteres calza con el contrato
 * (`discardDeadLetterRequestSchema` / `resolveQuarantineRequestSchema`).
 */
import { useEffect, useId, useRef, useState } from 'react';
import type { ReactElement } from 'react';
import { ErrorBanner } from './ErrorBanner';

export const MIN_REASON_LENGTH = 3;

export interface ReasonDialogProps {
  readonly title: string;
  readonly description: string;
  readonly fieldLabel: string;
  readonly confirmLabel: string;
  readonly busy: boolean;
  readonly error: unknown;
  readonly onConfirm: (reason: string) => void;
  readonly onCancel: () => void;
}

export function ReasonDialog({
  title,
  description,
  fieldLabel,
  confirmLabel,
  busy,
  error,
  onConfirm,
  onCancel,
}: ReasonDialogProps): ReactElement {
  const [reason, setReason] = useState('');
  const titleId = useId();
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    textareaRef.current?.focus();
  }, []);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        onCancel();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onCancel]);

  const trimmed = reason.trim();
  const valid = trimmed.length >= MIN_REASON_LENGTH;

  return (
    <div className="dialog__backdrop">
      <div className="dialog" role="dialog" aria-modal="true" aria-labelledby={titleId}>
        <h2 id={titleId}>{title}</h2>
        <p className="muted">{description}</p>

        <label className="field field--wide">
          <span className="field__label">{fieldLabel}</span>
          <textarea
            ref={textareaRef}
            className="textarea"
            value={reason}
            maxLength={512}
            onChange={(event) => setReason(event.target.value)}
          />
        </label>

        <ErrorBanner error={error} />

        <div className="dialog__actions">
          <button type="button" className="button" onClick={onCancel} disabled={busy}>
            Cancelar
          </button>
          <button
            type="button"
            className="button button--primary"
            onClick={() => onConfirm(trimmed)}
            disabled={busy || !valid}
          >
            {busy ? 'Enviando…' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
